const CARTA_GL_FILENAME_PATTERN = /^([a-z0-9-]+)_(\d{4}-\d{2}-\d{2})_gl-export\.(xlsx?|csv)$/i;
const SSC_GL_FILENAME_PATTERN = /^SSC_([A-Z0-9]+)_EntityGeneralLedger_/i;

const LEGAL_SUFFIX_MAP: Record<string, string> = {
  lp: 'L.P.',
  llc: 'LLC',
  llp: 'LLP',
  inc: 'Inc.',
  corp: 'Corp.',
  ltd: 'Ltd.',
};

const SSC_LEGAL_SUFFIXES = ['LLC', 'LLP', 'LP', 'INC', 'CORP', 'LTD'] as const;

export type GlSource = 'carta' | 'ssc';

export interface ParsedFundInfo {
  source: GlSource | null;
  fundName: string | null;
  date: string | null;
  error: string | null;
}

/**
 * Extract source (Carta vs SS&C), fund name, and date from a GL filename.
 *
 * SS&C exports look like `SSC_<ENTITYCODE>_EntityGeneralLedger_<window>_<ts>.{xlsx,csv}`
 * — the date window and snapshot timestamp are inside the filename, not parsed
 * here (the SS&C ingestion path stores the file as-is and the Atlas template
 * derives period info from the row data).
 *
 * Carta exports are `<fund-slug>_<YYYY-MM-DD>_gl-export.{xlsx,csv}`. Mirrors
 * backend logic in carta-ledger.service.ts:extractFundFromFilename — preview-only,
 * the backend uses file contents for authoritative fund detection.
 */
export function parseFundFromFilename(filename: string): ParsedFundInfo {
  const sscMatch = filename.match(SSC_GL_FILENAME_PATTERN);
  if (sscMatch) {
    return {
      source: 'ssc',
      fundName: humanizeSscEntity(sscMatch[1]),
      date: null,
      error: null,
    };
  }

  const cartaMatch = filename.match(CARTA_GL_FILENAME_PATTERN);
  if (!cartaMatch) {
    return {
      source: null,
      fundName: null,
      date: null,
      error: 'Filename does not match expected Carta or SS&C GL format',
    };
  }

  const fundSlug = cartaMatch[1];
  const date = cartaMatch[2];

  const words = fundSlug.split('-').map((word) => {
    const lower = word.toLowerCase();
    if (LEGAL_SUFFIX_MAP[lower]) {
      return LEGAL_SUFFIX_MAP[lower];
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  let fundName = words.join(' ');
  fundName = fundName.replace(/ (L\.P\.|LLC|LLP|Inc\.|Corp\.|Ltd\.)$/, ', $1');

  return { source: 'carta', fundName, date, error: null };
}

function humanizeSscEntity(code: string): string {
  const upper = code.toUpperCase();
  for (const suffix of SSC_LEGAL_SUFFIXES) {
    if (upper.endsWith(suffix)) {
      const base = upper.slice(0, -suffix.length);
      const display = LEGAL_SUFFIX_MAP[suffix.toLowerCase()] ?? suffix;
      const baseDisplay = base.charAt(0) + base.slice(1).toLowerCase();
      return `${baseDisplay} ${display}`.trim();
    }
  }
  return code;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
